// ST43 — minimal Projects tab: list projects, drill into members + per-project
// usage-by-tool. Backed by the Projects REST CRUD (GET /api/v1/projects...). A
// gateway predating the API 404s → the view shows an empty/unsupported state.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { Loading, ErrorState, Badge, bytesHuman } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { Drawer, DetailRow } from '../components/Drawer';
import type { Project, ProjectMember, UsageByTool } from '../api/schemas';

export function Projects() {
  const res = useResource(() => api.projects(), []);
  const [selected, setSelected] = useState<Project | null>(null);

  const cols: Column<Project>[] = [
    // Human NAME is the primary column; the opaque id is a hover tooltip (secondary), not the headline.
    { key: 'name', header: 'Project', cell: (p) => <b title={p.id}>{p.name}</b>, sortValue: (p) => p.name },
    { key: 'slug', header: 'Slug', cell: (p) => (p.slug ? <code className="mono small">{p.slug}</code> : '—') },
    { key: 'members', header: 'Members', align: 'right', cell: (p) => p.member_count ?? '—', sortValue: (p) => p.member_count ?? 0 },
    { key: 'created', header: 'Created', cell: (p) => (p.created_at ? new Date(p.created_at).toLocaleDateString() : '—') },
    {
      // Per-project storage: opens the browser scoped to this project's ``project/{id}`` namespace (ST35).
      key: 'storage',
      header: 'Storage',
      align: 'right',
      cell: (p) => (
        <Link
          className="btn btn-sm"
          to={`/objects?namespace=${encodeURIComponent(`project/${p.id}`)}`}
          onClick={(e) => e.stopPropagation()}
          title="Browse this project's storage"
        >
          Storage
        </Link>
      ),
    },
  ];

  return (
    <div className="view">
      <div className="view-head"><h1>Projects</h1></div>
      {res.loading ? (
        <Loading what="Loading projects…" />
      ) : res.error ? (
        // A gateway that predates the Projects API 404s — treat as unsupported, not a crash.
        res.error.includes('404') ? (
          <div className="empty">Projects are not available on this gateway.</div>
        ) : (
          <ErrorState message={res.error} onRetry={res.reload} />
        )
      ) : (
        <DataTable
          columns={cols}
          rows={res.data?.items ?? []}
          rowKey={(p) => p.id}
          empty="No projects."
          onRowClick={(p) => setSelected(p)}
        />
      )}
      {selected && <ProjectDrawer project={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ProjectDrawer({ project, onClose }: { project: Project; onClose: () => void }) {
  const members = useResource<{ items: ProjectMember[] }>(() => api.projectMembers(project.id), [project.id]);
  const usage = useResource<{ items: UsageByTool[] }>(() => api.usageByTool(project.id), [project.id]);

  const memberCols: Column<ProjectMember>[] = [
    { key: 'user', header: 'User', cell: (m) => m.name || m.user_email || m.user_id },
    { key: 'role', header: 'Role', cell: (m) => <Badge tone={m.role === 'owner' ? 'ok' : 'info'}>{m.role}</Badge> },
  ];
  const usageCols: Column<UsageByTool>[] = [
    { key: 'tool', header: 'Tool', cell: (u) => <code className="mono small">{u.tool}</code> },
    { key: 'calls', header: 'Calls', align: 'right', cell: (u) => u.calls, sortValue: (u) => u.calls },
    { key: 'gpu', header: 'GPU·s', align: 'right', cell: (u) => u.gpu_seconds.toFixed(1) },
    { key: 'bytes', header: 'Bytes', align: 'right', cell: (u) => bytesHuman(u.bytes) },
  ];

  return (
    <Drawer title={project.name} subtitle={<code className="mono small">{project.id}</code>} onClose={onClose} width={560}>
      {project.description && <DetailRow label="Description">{project.description}</DetailRow>}
      {project.network_id && <DetailRow label="Network"><code className="mono small">{project.network_id}</code></DetailRow>}

      <h4>Members</h4>
      {members.loading ? (
        <Loading what="Loading members…" />
      ) : members.error ? (
        <ErrorState message={members.error} onRetry={members.reload} />
      ) : (
        <DataTable columns={memberCols} rows={members.data?.items ?? []} rowKey={(m) => m.user_id} empty="No members." />
      )}

      <h4>Usage by tool</h4>
      {usage.loading ? (
        <Loading what="Loading usage…" />
      ) : usage.error ? (
        <ErrorState message={usage.error} onRetry={usage.reload} />
      ) : (
        <DataTable
          columns={usageCols}
          rows={usage.data?.items ?? []}
          rowKey={(u) => u.tool}
          empty="No usage for this project."
          initialSort={{ key: 'calls', dir: 'desc' }}
        />
      )}
    </Drawer>
  );
}
